/* eslint-disable react/prop-types */
import {
  Dialog,
  Box,
  Typography,
  Alert,
  Chip,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, alpha } from "@mui/material/styles";
import { ContentCopy } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function SubscriptionDialog({
  open,
  onClose,
  currentStatus,
  subscriptionData,
  firmAccounts,
  firmAccountsLoading,
  plans,
  isCheckingPayment,
  onCheckPaymentStatus,
  onNavigateToSubscription,
  copyToClipboard,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // Get selected plan details from subscription data
  const getSelectedPlanDetails = () => {
    if (!subscriptionData?.subscription_plan_id || !plans) return null;
    return plans.find(
      (plan) => plan.id === subscriptionData.subscription_plan_id
    );
  };

  // Helper function to format price
  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  // Helper function to format billing interval
  const formatBillingInterval = (interval) => {
    return interval === "MONTHLY"
      ? t("subscription.monthly", "Monthly")
      : t("subscription.yearly", "Yearly");
  };

  // Helper function to determine payment method type
  const getPaymentMethodType = () => {
    if (!subscriptionData || subscriptionData.status !== "PENDING") {
      return null;
    }

    // If iyzico_pricing_plan_reference_code has a value, it's an Iyzico payment
    if (subscriptionData.iyzico_pricing_plan_reference_code) {
      return "IYZICO";
    }

    // If it's null, it's a bank transfer
    return "BANK_TRANSFER";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 9999, // Ensure dialog is on top
        "& .MuiDialog-paper": {
          margin: 2,
          borderRadius: 2,
          overflow: "hidden",
          maxHeight: "90vh",
          zIndex: 10000, // Ensure paper is also on top
        },
      }}
    >
      {/* Dialog Header - Fixed */}
      <Box
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h5" component="div" fontWeight={600} gutterBottom>
          {currentStatus === "PENDING"
            ? getPaymentMethodType() === "IYZICO"
              ? t("subscription.iyzicoSessionTitle", "Continue Payment Session")
              : t("subscription.pendingTitle", "Subscription Payment Pending")
            : t("subscription.requiredTitle", "No Active Subscription")}
        </Typography>

        <Alert
          severity={
            currentStatus === "PENDING"
              ? getPaymentMethodType() === "IYZICO"
                ? "info"
                : "warning"
              : "info"
          }
          sx={{ mb: 2 }}
        >
          {currentStatus === "PENDING"
            ? getPaymentMethodType() === "IYZICO"
              ? t(
                  "subscription.iyzicoSessionMessage",
                  "You have an active payment session with Iyzico. Would you like to continue with your credit card payment?"
                )
              : t(
                  "subscription.pendingMessage",
                  "Your subscription is pending payment approval. Please complete your payment to activate your subscription."
                )
            : t(
                "subscription.requiredMessage",
                "You currently don't have any active subscription. To continue using the platform, please create a subscription."
              )}
        </Alert>

        {/* Current Status Display */}
        {currentStatus && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(
                currentStatus === "ACTIVE"
                  ? theme.palette.success.main
                  : currentStatus === "PENDING"
                    ? theme.palette.warning.main
                    : theme.palette.info.main,
                0.1
              ),
              border: `1px solid ${alpha(
                currentStatus === "ACTIVE"
                  ? theme.palette.success.main
                  : currentStatus === "PENDING"
                    ? theme.palette.warning.main
                    : theme.palette.info.main,
                0.3
              )}`,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                {t("subscription.currentStatus", "Current Status")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("subscription.lastChecked", "Last checked: {{time}}", {
                  time: new Date().toLocaleTimeString(),
                })}
              </Typography>
            </Box>
            <Chip
              label={t(`subscription.status${currentStatus}`, currentStatus)}
              color={
                currentStatus === "ACTIVE"
                  ? "success"
                  : currentStatus === "PENDING"
                    ? "warning"
                    : "default"
              }
              variant="filled"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 1,
              }}
            />
          </Box>
        )}

        <Typography color="text.secondary" variant="body2">
          {currentStatus === "PENDING"
            ? getPaymentMethodType() === "IYZICO"
              ? t(
                  "subscription.iyzicoSessionBenefits",
                  "Complete your payment to get full access to all platform features including work permits, observations, and more."
                )
              : t(
                  "subscription.pendingBenefitsMessage",
                  "Once your payment is approved, you'll have full access to all platform features including work permits, observations, and more."
                )
            : t(
                "subscription.benefitsMessage",
                "With an active subscription, you'll get access to all platform features including work permits, observations, and more."
              )}
        </Typography>
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          maxHeight: "60vh",
        }}
      >
        {/* Trial information for pending subscriptions */}
        {currentStatus === "PENDING" && subscriptionData?.trial_end_date && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.primary">
              {t(
                "subscription.trialAccessMessage",
                "You can continue using the platform until {{trialEndDate}} while your payment is being processed.",
                {
                  trialEndDate: new Date(
                    subscriptionData.trial_end_date
                  ).toLocaleDateString(),
                }
              )}
            </Typography>
          </Box>
        )}

        {/* Plan Summary for PENDING subscriptions */}
        {currentStatus === "PENDING" && subscriptionData && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {t("subscription.planSummary", "Plan Summary")}
            </Typography>

            <Card
              elevation={2}
              sx={{
                mb: 3,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.04),
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {getSelectedPlanDetails() && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {t("subscription.plan", "Plan")}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ mt: 0.5 }}
                        >
                          {getSelectedPlanDetails().name}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {t("subscription.transferAmount", "Transfer Amount")}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color="primary.main"
                          sx={{ mt: 0.5 }}
                        >
                          {formatPrice(
                            getSelectedPlanDetails().price,
                            getSelectedPlanDetails().currency
                          )}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {t("subscription.transferCurrency", "Currency")}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ mt: 0.5 }}
                        >
                          {getSelectedPlanDetails().currency}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {t("subscription.billingPeriod", "Billing Period")}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ mt: 0.5 }}
                        >
                          {formatBillingInterval(
                            getSelectedPlanDetails().billing_interval
                          )}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Bank Account Information for PENDING bank transfer subscriptions */}
        {currentStatus === "PENDING" &&
          getPaymentMethodType() === "BANK_TRANSFER" &&
          !firmAccountsLoading &&
          firmAccounts &&
          firmAccounts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                {t("subscription.bankTransferDetails", "Bank Transfer Details")}
              </Typography>

              {firmAccounts.map((account, index) => (
                <Card
                  key={account.id}
                  elevation={2}
                  sx={{
                    mb: index < firmAccounts.length - 1 ? 2 : 0,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      {/* Bank Name and Currency Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          color="primary"
                        >
                          {account.bank_name}
                        </Typography>
                        <Chip
                          label={account.currency}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>

                      <Divider />

                      {/* Account Details Grid */}
                      <Grid container spacing={2}>
                        {/* IBAN */}
                        {account.iban && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {t("subscription.iban", "IBAN")}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                  sx={{ flex: 1 }}
                                >
                                  {account.iban}
                                </Typography>
                                <Tooltip
                                  title={t(
                                    "subscription.copyIban",
                                    "Copy IBAN"
                                  )}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      copyToClipboard(account.iban, "IBAN")
                                    }
                                    sx={{
                                      color: "primary.main",
                                      "&:hover": {
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* SWIFT Code */}
                        {account.swift_code && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {t("subscription.swiftCode", "SWIFT Code")}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                  sx={{ flex: 1 }}
                                >
                                  {account.swift_code}
                                </Typography>
                                <Tooltip
                                  title={t(
                                    "subscription.copySwift",
                                    "Copy SWIFT Code"
                                  )}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      copyToClipboard(
                                        account.swift_code,
                                        "SWIFT Code"
                                      )
                                    }
                                    sx={{
                                      color: "primary.main",
                                      "&:hover": {
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Bank Code */}
                        {account.bank_code && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {t("subscription.bankCode", "Bank Code")}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                  sx={{ flex: 1 }}
                                >
                                  {account.bank_code}
                                </Typography>
                                <Tooltip
                                  title={t(
                                    "subscription.copyBankCode",
                                    "Copy Bank Code"
                                  )}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      copyToClipboard(
                                        account.bank_code,
                                        "Bank Code"
                                      )
                                    }
                                    sx={{
                                      color: "primary.main",
                                      "&:hover": {
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Account Number */}
                        {account.account_number && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {t(
                                  "subscription.accountNumber",
                                  "Account Number"
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                                sx={{ mt: 0.5 }}
                              >
                                {account.account_number}
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                        {/* Routing Number */}
                        {account.routing_number && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {t(
                                  "subscription.routingNumber",
                                  "Routing Number"
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                                sx={{ mt: 0.5 }}
                              >
                                {account.routing_number}
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                        {/* Account Type */}
                        {account.account_type && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {t("subscription.accountType", "Account Type")}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {account.account_type}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>

                      {/* Bank Address */}
                      {account.bank_address && (
                        <>
                          <Divider />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              {t("subscription.bankAddress", "Bank Address")}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {account.bank_address}
                            </Typography>
                          </Box>
                        </>
                      )}

                      {/* Beneficiary Address */}
                      {account.beneficiary_address && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            {t(
                              "subscription.beneficiaryAddress",
                              "Beneficiary Address"
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {account.beneficiary_address}
                          </Typography>
                        </Box>
                      )}

                      {/* Special Instructions */}
                      {account.special_instructions && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>
                              {t(
                                "subscription.specialInstructions",
                                "Special Instructions"
                              )}
                              :
                            </strong>{" "}
                            {account.special_instructions}
                          </Typography>
                        </Alert>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
      </Box>

      {/* Fixed Footer with Buttons */}
      <Box
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 500 }}>
          {t("common.later", "Later")}
        </Button>
        <Button
          onClick={() => {
            if (currentStatus === "PENDING") {
              if (getPaymentMethodType() === "IYZICO") {
                // Continue with Iyzico payment - navigate to subscription form
                onNavigateToSubscription();
              } else {
                // Check bank transfer payment status
                onCheckPaymentStatus();
              }
            } else {
              onNavigateToSubscription();
            }
          }}
          variant="contained"
          color="primary"
          disabled={isCheckingPayment}
          sx={{
            px: 3,
            py: 1,
            fontWeight: 600,
            boxShadow: 1,
            "&:hover": {
              boxShadow: 2,
            },
            minWidth: 180, // Ensure consistent width during loading
          }}
        >
          {currentStatus === "PENDING"
            ? getPaymentMethodType() === "IYZICO"
              ? t("subscription.continuePayment", "Continue Payment")
              : isCheckingPayment
                ? t("subscription.checking", "Checking...")
                : t("subscription.checkPayment", "Check Payment Status")
            : t("subscription.createSubscription", "Create Subscription")}
        </Button>
      </Box>
    </Dialog>
  );
}

SubscriptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentStatus: PropTypes.string,
  subscriptionData: PropTypes.object,
  firmAccounts: PropTypes.array,
  firmAccountsLoading: PropTypes.bool,
  plans: PropTypes.array,
  isCheckingPayment: PropTypes.bool.isRequired,
  onCheckPaymentStatus: PropTypes.func.isRequired,
  onNavigateToSubscription: PropTypes.func.isRequired,
  copyToClipboard: PropTypes.func.isRequired,
};

export default SubscriptionDialog;
